import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { LoadingService } from '@/core/services/loading.service';
import { CurrentUser } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { CrmWebhookEvent, CrmWebhookService } from '@/core/services/crm-webhook.service';
import {
    Subject,
    takeUntil,
    switchMap,
    distinctUntilChanged,
    filter,
    Observable,
    defer,
    catchError,
    finalize,
    EMPTY,
    debounce,
    merge,
    timer,
} from 'rxjs';
import { LogoService } from '@/core/services/logo.service';
import { FormBuilder } from '@angular/forms';
import { AccountSettingsForm } from '@/core/models/account';
import { map, tap } from 'rxjs/operators';
import { RetroactiveEditLimitFormFacade } from '@/home/settings/general/retroactive-edit-limit.form';
import { Account } from '@/core/models/account';
import { Logo } from '@/core/models/logo-types';
import { StdToasts, ToastService } from '@/core/services/toast.service';
import { ReportSettingsService } from '@/core/services/report-settings/report-settings.service';

@Component({
    selector: 'app-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    standalone: false,
})
export class GeneralComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly flush$ = new Subject<void>();
    companyName?: string;
    retroActiveEditLimit?: number | null;
    timeBudgetsEnabled = false;
    timeBudgetsVisibleToAllUsers = false;
    isMultiUserAccount = false;
    currentUser?: CurrentUser;
    companyLogo?: SafeUrl | null;
    selectedFile: File | null = null;
    licenseStatePro = false;
    isInitDone = false;
    readonly retroactiveEditForm: RetroactiveEditLimitFormFacade;

    constructor(
        private readonly crmWebhookService: CrmWebhookService,
        private readonly userService: UserService,
        private readonly reportSettingsService: ReportSettingsService,
        private readonly loadingService: LoadingService,
        private readonly toastService: ToastService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly logoService: LogoService,
        private readonly formBuilder: FormBuilder,
    ) {
        this.retroactiveEditForm = new RetroactiveEditLimitFormFacade(this.formBuilder);
    }

    public ngOnDestroy(): void {
        this.flush$.next(); // flush the stream in case there are pending changes that have not been saved due to the debounce time yet.
        this.flush$.complete();

        this.destroy$.next();
        this.destroy$.complete();
        this.retroactiveEditForm.destroy();
    }

    public ngOnInit(): void {
        void this.initAsync();
    }

    private async initAsync(): Promise<void> {
        this.loadingService.setLoading(true);

        const account: Account = await this.userService.getAccount();
        this.companyName = account.accountSettings.companyName ?? '';
        this.retroActiveEditLimit = account.accountSettings.retroactiveEditLimit ?? undefined;
        this.timeBudgetsEnabled = account.accountSettings.timeBudgetsEnabled === true;
        this.timeBudgetsVisibleToAllUsers = account.accountSettings.timeBudgetsVisibleToAllUsers === true;
        this.isMultiUserAccount = !account.isSingleUserAccount();

        this.currentUser = await this.userService.getCurrentUser();
        this.licenseStatePro = this.currentUser.isPro();

        this.logoService.clearLegacyCache();
        // since only admins see the general settings page, only fetch logo for admins
        if (this.currentUser.isAdmin()) {
            this.logoService
                .getLogo(this.currentUser.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (logo: Logo) => {
                        this.companyLogo = logo;
                    },
                    error: () => {
                        this.companyLogo = null;
                        this.toastService.error('standard_error', 'standard_failure_logo_fetch');
                    },
                });
        }
        this.initDone();
    }

    private initDone(): void {
        this.isInitDone = true;
        this.loadingService.setLoading(false);
        this.updateRetroActiveEditingFormByModel();
        this.registerRestroActiveEditChangeHook();
    }

    private registerRestroActiveEditChangeHook(): void {
        this.retroactiveEditForm.form.valueChanges
            .pipe(
                filter(() => this.retroactiveEditForm.form.valid),
                debounce(() => merge(timer(1000), this.flush$)), // delay automatic save to avoid triggering API changes too frequently
                map(() => this.retroactiveEditForm.getValue()),
                // only save when value has changed
                distinctUntilChanged(),
                // immer nur den letzten Save laufen lassen
                switchMap((value) => this.saveRetroActiveEditLimit$(value)),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Updates the form by the value of "retroActiveEditLimit"
     */
    private updateRetroActiveEditingFormByModel(): void {
        if (this.retroActiveEditLimit === undefined) {
            return;
        }
        this.retroactiveEditForm.setValue(this.retroActiveEditLimit);
    }

    /**
     * Updates "retroActiveEditLimit" by the form values
     */
    private updateModelByRetroActiveEditingForm(): void {
        this.retroActiveEditLimit = this.retroactiveEditForm.getValue();
    }

    public saveRetroActiveEditLimit$(_value: number | null): Observable<unknown> {
        this.updateModelByRetroActiveEditingForm();

        if (this.retroActiveEditLimit === undefined) {
            return EMPTY;
        }

        return this.saveAccountSettings$({
            retroactiveEditLimit: this.retroActiveEditLimit,
        });
    }

    public saveCompanyName(): void {
        if (this.companyName === undefined) {
            return;
        }
        if (this.currentUser) {
            void this.crmWebhookService.send(CrmWebhookEvent.updateProfile, {
                email: this.currentUser.email,
                company: this.companyName,
            });
        }
        this.saveAccountSettings({
            companyName: this.companyName,
        });
    }

    /**
     * Saves the account's time budget switch; the toggle is reset when saving fails.
     */
    public saveTimeBudgetsEnabled(enabled: boolean): void {
        this.saveToggle({ timeBudgetsEnabled: enabled }, () => (this.timeBudgetsEnabled = !enabled));
    }

    /**
     * Saves whether regular users can view time budgets; the toggle is reset when saving fails.
     */
    public saveTimeBudgetsVisibleToAllUsers(visible: boolean): void {
        this.saveToggle(
            { timeBudgetsVisibleToAllUsers: visible },
            () => (this.timeBudgetsVisibleToAllUsers = !visible),
        );
    }

    private saveToggle(accountSettings: AccountSettingsForm, resetToggle: () => void): void {
        let saved = false;
        this.saveAccountSettings$(accountSettings)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    saved = true;
                },
                complete: () => {
                    // saveAccountSettings$ completes without a value when the request failed
                    if (!saved) {
                        resetToggle();
                    }
                },
            });
    }

    private saveAccountSettings$(accountSettings: AccountSettingsForm): Observable<unknown> {
        // defer => loading/toasts werden erst beim Subscribe ausgeführt (wichtig für switchMap)
        return defer(() => {
            this.loadingService.setLoading(true);

            return this.userService.patchAccountSettings(accountSettings).pipe(
                tap(() => {
                    this.toastService.toast(StdToasts.SAVE_SUCCESS);
                }),
                catchError((_error) => {
                    this.toastService.toast(StdToasts.STANDARD_ERROR);
                    return EMPTY; // assures that the stream survives the error
                }),
                finalize(() => {
                    this.loadingService.setLoading(false);
                }),
            );
        });
    }

    private saveAccountSettings(accountSettings: AccountSettingsForm): void {
        this.saveAccountSettings$(accountSettings).pipe(takeUntil(this.destroy$)).subscribe();
    }

    /**
     * Removes the user's uploaded logo from the server and resets to default.
     */
    public removeLogo(): void {
        if (!this.currentUser) {
            return;
        }
        const currUser = this.currentUser;
        this.logoService
            .deleteLogo(currUser.id)
            .pipe(
                switchMap(() => {
                    // should reset the user's logoOption to an unset value => null
                    // TODO removal of the logo has to properly reset the logoOption for all users of the account, not only for the current user
                    return this.reportSettingsService.updateReportSettings({
                        logoOption: null,
                    });
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.companyLogo = null;
                    this.changeDetector.detectChanges();
                    this.toastService.success('standard_success', 'standard_success_logo_deleted');
                },
                error: () => {
                    this.toastService.error('standard_error', 'standard_failure_logo_deleted');
                },
            });
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input || !input.files || input.files.length === 0) {
            return;
        }
        this.selectedFile = input.files[0];
        this.uploadFile();
        // Reset the input value after file selection
        input.value = '';
    }

    uploadFile(): void {
        //  check if pro license
        if (!this.licenseStatePro) {
            return;
        }
        if (!this.selectedFile) {
            console.error('No file selected for upload.');
            return;
        }

        const userId = this.currentUser?.id;
        if (!userId) {
            return;
        }

        this.logoService.uploadLogo(this.selectedFile, userId).subscribe({
            next: (logo: string) => {
                this.companyLogo = logo;
                this.changeDetector.detectChanges();
                this.toastService.success('standard_success', 'standard_success_saved');
                this.loadingService.setLoading(false);
            },
            error: (error: Error) => {
                const errorMessage = error.message;
                if (errorMessage.includes('file type')) {
                    this.toastService.error('standard_error', 'failure_upload_file_type_invalid');
                } else if (errorMessage.includes('File size')) {
                    this.toastService.error('standard_error', 'failure_upload_file_size_exceeded');
                } else {
                    this.toastService.error('standard_error', 'profile_profilesavingerror');
                }
            },
        });
    }
}
